/**
 * Read-only viewer dialogs for the Cash Flows hub — Access "Need to map" /
 * "Duplicate Data" / "Misa Vender/Customer" / "Misa Invoices" buttons open a
 * subform-hosting popup. On web: one Dialog wrapping the shared list grid
 * (non-selectable). Data comes from the matching `v_report_cashflow_*` views.
 */
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { TEXT_TRUNCATE } from '../../../theme/responsive'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowMapCpNeedQuery,
  useGetCashflowMapCpDupQuery,
  useGetCashflowMisaObjectQuery,
  useGetCashflowMapServiceQuery,
  useGetCashflowMapServiceDupQuery,
  useGetCashflowMapMuaHangNeedQuery,
  useGetCashflowMisaMuaHangQuery,
  useGetCashflowMapBanHangQuery,
  useGetCashflowMisaBanHangQuery,
} from '../../../store/api/dashboardApi'
import CashflowDataList, { type CashflowColumn } from './CashflowDataList'
import {
  CP_DUP_COLUMNS,
  CP_MISA_COLUMNS,
  CP_NEED_COLUMNS,
  SERVICE_COLUMNS,
  SERVICE_NEED_COLUMNS,
  MISA_OBJECT_COLUMNS,
  MUAHANG_COLUMNS,
  MUAHANG_NEED_COLUMNS,
  BANHANG_NEED_COLUMNS,
  type CashflowAction,
} from './cashflowPages'

type Row = Record<string, unknown>

// Widths sized to value: flag/date/currency compact, VND amounts wide, text wrap.
const MISA_MUAHANG_COLUMNS: CashflowColumn<Row>[] = [
  { key: 'mappedTxt', label: 'Is Mapped?', filter: 'checkboxes', width: 175 },
  { key: 'tabName', label: 'Tab Name', width: 130 },
  { key: 'sochungtu', label: 'Voucher Number', width: 150 },
  { key: 'ngayhachtoan', label: 'Voucher Date', kind: 'date', width: 125 },
  { key: 'nhacungcap', label: 'Vender', wrap: true, width: 220 },
  { key: 'sohoadon', label: 'Invoice No', width: 130 },
  { key: 'ngayhoadon', label: 'Invoice Date', kind: 'date', width: 125 },
  { key: 'loaitien', label: 'Currency', width: 105 },
  { key: 'tygia', label: 'Exchange Rate', kind: 'number', align: 'right', width: 130 },
  { key: 'inOutwardRefno', label: 'In/Out Refno', width: 130 },
  { key: 'thanhtienGoc', label: 'Amount (raw)', kind: 'number', align: 'right', width: 155 },
  { key: 'thanhtienQuydoi', label: 'Amount (convert)', kind: 'number', align: 'right', width: 165 },
  { key: 'thanhtoanGoc', label: 'Payment (raw)', kind: 'number', align: 'right', width: 155 },
  { key: 'thanhtoanQuydoi', label: 'Payment (convert)', kind: 'number', align: 'right', width: 170 },
  { key: 'giatrinhapkho', label: 'In/Out Amount', kind: 'number', align: 'right', width: 145 },
  { key: 'journalMemo', label: 'Description', wrap: true, width: 440 },
  { key: 'refid', label: 'Refid', width: 120 },
]

// Access `frmReportCashFlowQryBanHang_misa` datasheet — 25 columns, physical order
// (ColumnOrder all 0 → TabIndex). Captions verbatim from the form labels.
const MISA_BANHANG_COLUMNS: CashflowColumn<Row>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'tabName', label: 'TabName', width: 120 },
  { key: 'ngayhachtoan', label: 'PostedDate', kind: 'date', width: 125 },
  { key: 'sochungtu', label: 'VoucherNo', width: 130 },
  { key: 'makhachhang', label: 'CustomerCode', width: 140 },
  { key: 'ngaychungtu', label: 'VoucherDate', kind: 'date', width: 125 },
  { key: 'loaitien', label: 'Currency', width: 105 },
  { key: 'tygia', label: 'FX', kind: 'number', align: 'right', width: 105 },
  { key: 'phieuKho', label: 'In/OutWardNo', width: 130 },
  { key: 'tongTienHang', label: 'In/OutAmount', kind: 'number', align: 'right', width: 145 },
  { key: 'tienChietKhau', label: 'DiscountAmount', kind: 'number', align: 'right', width: 150 },
  { key: 'tienThueGtgt', label: 'VATAmount', kind: 'number', align: 'right', width: 135 },
  { key: 'tongTienThanhToan', label: 'PaymentAmount', kind: 'number', align: 'right', width: 150 },
  { key: 'dienGiai', label: 'Description', wrap: true, width: 300 },
  { key: 'refId', label: 'RefID', width: 110 },
  { key: 'ctnvkOverviewId', label: 'OtherVoucherID', width: 140 },
  { key: 'misaContract', label: 'MisaContract', wrap: true, width: 170 },
  { key: 'misaSohoadon', label: 'MisaInvoiceNo', width: 140 },
  { key: 'contractExtract', label: 'MisaContract_Extract', wrap: true, width: 200 },
  { key: 'invExtract', label: 'MisaInvoice_Extract', wrap: true, width: 200 },
  { key: 'erpCpCode', label: 'ERPCode', width: 120 },
  { key: 'erpCpName', label: 'ERPName', wrap: true, width: 280 },
  { key: 'erpInv', label: 'ERP-Map-InvoiceNo', width: 160 },
  { key: 'contractNo', label: 'ERP-Map-ContractNo', width: 170 },
  { key: 'khachhangName', label: 'CustomerName', wrap: true, width: 280 },
]


const TITLES: Record<string, string> = {
  'cp-need': 'CP Mapping - Objects need to map',
  'cp-dup': 'CP Mapping - Duplicate Data',
  'cp-misa': 'CP Mapping - Misa Vender/Customer',
  'svc-need': 'Service Mapping - Objects need to map',
  'svc-dup': 'Service Mapping - Duplicate Data',
  'svc-misa': 'Service Mapping - Misa Vender/Customer',
  'mh-need': 'Purchase Invoice - Need to map',
  'mh-misa': 'Purchase Invoice - Misa Invoices',
  'bh-need': 'Sale Invoice - Need to map',
  'bh-misa': 'Sale Invoice - Misa Invoices',
}

interface Props {
  action: CashflowAction | null
  onClose: () => void
  /** Access "Objects need to map" → "Edit Mapping" button: open the CP/Service
   *  edit dialog seeded from the selected row (wired for `cp-need` & `svc-need`;
   *  parent branches on the current action). */
  canEdit?: boolean
  onEditMapping?: (row: Row) => void
  /** Filter Date range → scopes mh-need (sohoadon_checkdate) + bh-need (contract_date). */
  dateRange?: { startDate?: string | null; endDate?: string | null }
}

export default function CashflowViewerDialog({ action, onClose, canEdit = false, onEditMapping, dateRange }: Props) {
  const open = action !== null && action in TITLES
  const [selectedId, setSelectedId] = useState<string | number | null>(null)
  // Reset selection whenever the viewer opens a different action.
  useEffect(() => setSelectedId(null), [action])

  // refetchOnMountOrArgChange: reopening a viewer (or switching which one) fetches
  // fresh data, not stale RTK cache (memory dashboard_reload_refetch).
  const RO = { refetchOnMountOrArgChange: true }
  const cpNeed = useGetCashflowMapCpNeedQuery(undefined, { skip: action !== 'cp-need', ...RO })
  const cpDup = useGetCashflowMapCpDupQuery(undefined, { skip: action !== 'cp-dup', ...RO })
  const svc = useGetCashflowMapServiceQuery(undefined, { skip: action !== 'svc-need', ...RO })
  const svcDup = useGetCashflowMapServiceDupQuery(undefined, { skip: action !== 'svc-dup', ...RO })
  const misaObj = useGetCashflowMisaObjectQuery(undefined, { skip: action !== 'cp-misa' && action !== 'svc-misa', ...RO })
  const mhNeed = useGetCashflowMapMuaHangNeedQuery(dateRange ?? undefined, { skip: action !== 'mh-need', ...RO })
  const misaMh = useGetCashflowMisaMuaHangQuery(dateRange ?? undefined, { skip: action !== 'mh-misa', ...RO })
  const bh = useGetCashflowMapBanHangQuery(dateRange ?? undefined, { skip: action !== 'bh-need', ...RO })
  const misaBh = useGetCashflowMisaBanHangQuery(dateRange ?? undefined, { skip: action !== 'bh-misa', ...RO })

  const cfg = useMemo(() => {
    switch (action) {
      case 'cp-need':
        return { q: cpNeed, columns: CP_NEED_COLUMNS, rows: cpNeed.data?.items ?? [], id: (r: Row) => String(r.erpCode ?? '') }
      case 'cp-dup':
        return { q: cpDup, columns: CP_DUP_COLUMNS, rows: cpDup.data?.items ?? [], id: (r: Row) => String(r.erpCode ?? '') }
      case 'cp-misa':
        return { q: misaObj, columns: CP_MISA_COLUMNS, rows: misaObj.data?.items ?? [], id: (r: Row) => String(r.misaId ?? '') }
      case 'svc-misa':
        return { q: misaObj, columns: MISA_OBJECT_COLUMNS, rows: misaObj.data?.items ?? [], id: (r: Row) => String(r.misaId ?? '') }
      case 'svc-need':
        // Access `_need`: WHERE is_mapped = 'N' (the view returns 'Y'/'N'). The
        // generic isUnmapped() set has no 'N' → it filtered everything out and
        // the list showed empty. Match Access exactly.
        return {
          q: svc,
          columns: SERVICE_NEED_COLUMNS,
          rows: (svc.data?.items ?? []).filter(
            (r) => String((r as unknown as Row).isMapped ?? '').trim().toUpperCase() === 'N',
          ),
          id: (r: Row) => String(r.serviceCode ?? ''),
        }
      case 'svc-dup':
        return { q: svcDup, columns: SERVICE_COLUMNS, rows: svcDup.data?.items ?? [], id: (r: Row) => String(r.serviceCode ?? '') }
      case 'mh-need':
        return { q: mhNeed, columns: MUAHANG_NEED_COLUMNS, rows: mhNeed.data?.items ?? [], id: (r: Row) => String(r.erpInvNo ?? '') }
      case 'mh-misa':
        return { q: misaMh, columns: MISA_MUAHANG_COLUMNS, rows: misaMh.data?.items ?? [], id: (r: Row) => String(r.refid ?? '') }
      case 'bh-need':
        // Access `_need`: WHERE is_mapped = 'N' (+ a contract_date range from
        // tmp_filter_date, module='report' — not available client-side). The
        // 'N' value isn't in isUnmapped()'s set → the list showed empty. Match
        // the is_mapped='N' condition exactly (same fix as svc-need).
        return {
          q: bh,
          columns: BANHANG_NEED_COLUMNS,
          rows: (bh.data?.items ?? []).filter(
            (r) => String((r as unknown as Row).isMapped ?? '').trim().toUpperCase() === 'N',
          ),
          // Unique per ERP invoice row (a contract has many invoices) → focusing
          // a row loads THAT row into Edit Mapping, not the first same-contract one.
          id: (r: Row) => `${String(r.contractNo ?? '')}|${String(r.erpInv ?? '')}`,
        }
      case 'bh-misa':
        return { q: misaBh, columns: MISA_BANHANG_COLUMNS, rows: misaBh.data?.items ?? [], id: (r: Row) => String(r.refId ?? '') }
      default:
        return null
    }
  }, [action, cpNeed, cpDup, misaObj, svc, svcDup, mhNeed, misaMh, bh, misaBh])

  // Access `_need` "Edit Mapping" → open the CP/Service/Purchase edit seeded from the row.
  const canPickRow =
    (action === 'cp-need' || action === 'svc-need' || action === 'mh-need' || action === 'bh-need' ||
      action === 'mh-misa' || action === 'bh-misa') &&
    !!onEditMapping
  const selectedRow = useMemo(
    () => (canPickRow && cfg ? (cfg.rows as Row[]).find((r) => cfg.id(r) === selectedId) ?? null : null),
    [canPickRow, cfg, selectedId],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '88vh' } }}>
      <DialogTitle sx={{ py: 1, pr: 6, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          variant="subtitle1"
          fontWeight={700}
          noWrap
          title={action ? TITLES[action] : ''}
          sx={{ ...TEXT_TRUNCATE, flex: 1, minWidth: 0 }}
        >
          {action ? TITLES[action] : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
        {cfg && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <CashflowDataList
              columns={cfg.columns as CashflowColumn<Row>[]}
              rows={cfg.rows as Row[]}
              getRowId={cfg.id}
              loading={cfg.q.isLoading}
              fetching={cfg.q.isFetching}
              error={!!cfg.q.error}
              errorTitle="Could not load data"
              emptyTitle="No records found"
              searchPlaceholder="Search…"
              selectable={canPickRow}
              selectedId={selectedId}
              onSelect={setSelectedId}
              toolbar={
                canPickRow ? (
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                    disabled={!canEdit || !selectedRow}
                    onClick={() => {
                      if (selectedRow) onEditMapping?.(selectedRow)
                    }}
                    sx={{ py: 0.2, px: 0.75, minHeight: 26, fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    Edit Mapping
                  </Button>
                ) : undefined
              }
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
